import type { PostgrestError } from '@supabase/supabase-js';

import type { ServiceError as IServiceError } from './ServiceTypes';

export class ServiceError extends Error implements IServiceError {
  code: string;
  details?: any;
  originalError?: PostgrestError | Error;

  constructor(
    code: string,
    message: string,
    details?: any,
    originalError?: PostgrestError | Error,
  ) {
    super(message);
    this.name = 'ServiceError';
    this.code = code;
    this.details = details;
    this.originalError = originalError;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ServiceError);
    }
  }

  static fromPostgrestError(error: PostgrestError): ServiceError {
    return new ServiceError(
      error.code || 'DATABASE_ERROR',
      error.message || 'An error occurred while accessing the database',
      error.details,
      error,
    );
  }

  /**
   * Normalize any error thrown by a Supabase call into a ServiceError.
   * Supabase query errors are PostgrestError-shaped; other failures may be
   * plain Errors. Accepts `unknown` so every call site type-checks regardless
   * of how its caught/destructured error is statically typed.
   */
  static fromSupabaseError(error: unknown): ServiceError {
    if (error instanceof ServiceError) {
      return error;
    }
    if (error instanceof Error) {
      return ServiceError.fromError(error);
    }
    return ServiceError.fromPostgrestError(error as PostgrestError);
  }

  static fromError(error: Error): ServiceError {
    if (error instanceof ServiceError) {
      return error;
    }

    return new ServiceError(
      'UNKNOWN_ERROR',
      error.message || 'An unexpected error occurred',
      undefined,
      error,
    );
  }

  static notFound(resource: string): ServiceError {
    return new ServiceError('NOT_FOUND', `${resource} not found`, { resource });
  }

  static unauthorized(message?: string): ServiceError {
    return new ServiceError(
      'UNAUTHORIZED',
      message || 'You are not authorized to perform this action',
    );
  }

  static validationError(message: string, details?: any): ServiceError {
    return new ServiceError('VALIDATION_ERROR', message, details);
  }

  static networkError(message?: string): ServiceError {
    return new ServiceError(
      'NETWORK_ERROR',
      message || 'A network error occurred. Please check your connection and try again.',
    );
  }

  static forbidden(message?: string): ServiceError {
    return new ServiceError('FORBIDDEN', message || 'You do not have permission to do this');
  }

  static conflict(message: string, details?: any): ServiceError {
    return new ServiceError('CONFLICT', message, details);
  }

  static operationError(message: string, details?: any): ServiceError {
    return new ServiceError('OPERATION_ERROR', message, details);
  }

  toJSON(): IServiceError {
    return {
      code: this.code,
      message: this.message,
      details: this.details,
      originalError: this.originalError,
    };
  }

  /**
   * Pulls a human-readable message out of a Supabase FunctionsHttpError.
   * The SDK's `functions.invoke()` surfaces only a generic "Edge Function
   * returned a non-2xx status code" message on `error.message` — that's a
   * client-library constant, not anything from the response body. The real
   * reason the edge function rejected the request is in the Response stashed
   * on `error.context`, only reachable via `error.context.json()`.
   */
  static async extractEdgeFunctionMessage(error: unknown, fallback?: string): Promise<string> {
    const fallbackMessage =
      fallback ?? (error instanceof Error ? error.message : 'Edge function failed');
    const context = (error as { context?: Response })?.context;
    if (!context || typeof context.clone !== 'function') return fallbackMessage;
    try {
      const body = await context.clone().json();
      if (body && typeof body === 'object') {
        const msg =
          (body as { error?: unknown; message?: unknown }).error ??
          (body as { error?: unknown; message?: unknown }).message;
        if (typeof msg === 'string' && msg.trim()) return msg;
      }
    } catch {
      try {
        const text = await context.clone().text();
        if (text.trim()) return text.slice(0, 300);
      } catch {
        // fall through
      }
    }
    return fallbackMessage;
  }
}
